import { useEffect } from "react";

import PropTypes from "prop-types";

import {
  company,
  getAssetUrl,
  getSiteUrl,
  getImageProperties,
  getMobileBackgroundImage,
} from "studio-edifique/siteContent";

const upsertMetaTag = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([attribute, value]) => {
    if (value) {
      element.setAttribute(attribute, value);
    }
  });
};

const upsertLinkTag = (rel, href) => {
  if (!href) {
    return;
  }

  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  element.setAttribute("rel", rel);
  element.setAttribute("href", href);
};

function SeoHead({
  title,
  description,
  path = "/",
  image,
  imageSizes,
  responsiveBackground = false,
  type = "website",
  keywords,
  noIndex = false,
  schema = [],
}) {
  useEffect(() => {
    const pageTitle = `${title} | Studio Edifique`;
    const canonicalUrl = getSiteUrl(path);
    const socialImage = getAssetUrl(image);

    document.documentElement.lang = "pt-BR";
    document.title = pageTitle;

    upsertMetaTag('meta[name="author"]', {
      name: "author",
      content: company.brandName,
    });
    upsertMetaTag('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertMetaTag('meta[name="keywords"]', {
      name: "keywords",
      content: keywords.join(", "),
    });
    upsertMetaTag('meta[name="robots"]', {
      name: "robots",
      content: noIndex ? "noindex, follow" : "index, follow",
    });
    upsertMetaTag('meta[property="og:locale"]', {
      property: "og:locale",
      content: "pt_BR",
    });
    upsertMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: company.brandName,
    });
    upsertMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: pageTitle,
    });
    upsertMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });
    upsertMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    upsertMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: pageTitle,
    });
    upsertMetaTag('meta[name="twitter:url"]', {
      name: "twitter:url",
      content: canonicalUrl,
    });
    upsertMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: socialImage ? "summary_large_image" : "summary",
    });

    if (socialImage) {
      upsertMetaTag('meta[property="og:image"]', {
        property: "og:image",
        content: socialImage,
      });
      upsertMetaTag('meta[name="twitter:image"]', {
        name: "twitter:image",
        content: socialImage,
      });
    }

    if (noIndex) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    } else {
      upsertLinkTag("canonical", canonicalUrl);
    }

    Array.from(document.head.querySelectorAll('script[data-studio-schema="true"]')).forEach(
      (item) => item.remove()
    );

    schema.forEach((item, index) => {
      const script = document.createElement("script");

      script.type = "application/ld+json";
      script.dataset.studioSchema = "true";
      script.id = `studio-schema-${index}`;
      script.text = JSON.stringify(item);

      document.head.appendChild(script);
    });
    document.head
      .querySelectorAll('link[data-studio-preload="true"]')
      .forEach((link) => link.remove());
    if (image && !noIndex) {
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = image;
      preload.dataset.studioPreload = "true";
      preload.setAttribute("fetchpriority", "high");
      if (responsiveBackground) {
        preload.media = "(min-width: 601px)";
        const mobilePreload = preload.cloneNode();
        mobilePreload.href = getMobileBackgroundImage(image);
        mobilePreload.media = "(max-width: 600px)";
        document.head.appendChild(mobilePreload);
      }
      const properties = getImageProperties(image);
      if (imageSizes && properties.srcSet) {
        preload.setAttribute("imagesrcset", properties.srcSet);
        preload.setAttribute("imagesizes", imageSizes);
      }
      document.head.appendChild(preload);
    }
    document.documentElement.dataset.seoReady = path;
  }, [
    description,
    image,
    imageSizes,
    responsiveBackground,
    keywords,
    noIndex,
    path,
    schema,
    title,
    type,
  ]);

  return null;
}

SeoHead.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  path: PropTypes.string,
  image: PropTypes.string,
  imageSizes: PropTypes.string,
  responsiveBackground: PropTypes.bool,
  type: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
  schema: PropTypes.arrayOf(PropTypes.object),
  noIndex: PropTypes.bool,
};

export default SeoHead;
